import {
  useQuery,
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { useSnackbar } from "notistack";

import { withJWTClient } from "./connectClient";

import {
  cloneBroadcastResponseSchema,
  createBroadcastResponseSchema,
  getBroadcastResponseSchema,
  listBroadcastsResponseSchema,
  updateBroadcastResponseSchema,
  type AudiencesBroadcastStatus,
  type AudiencesCreateBroadcastRequestV2,
  type BroadcastVariation,
  type CreateBroadcastRequestV2,
  type CreateBroadcastResponse,
  type CloneBroadcastResponse,
  type GetBroadcastResponse,
  type ListBroadcastsResponse,
  type UpdateBroadcastRequestV2,
  type UpdateBroadcastResponse,
} from "@/features/BroadcastV2/types";
import { enqueueErrorSnackbar } from "@/utils/snackbarHelpers";

const API_BASE_URL: string = import.meta.env.DEV
  ? "/audiences"
  : (import.meta.env.VITE_AUDIENCES_API_URL as string) || "";

// --- Create ---

export async function createBroadcastV2ViaAudiencesApi(
  siteId: string,
  broadcast: CreateBroadcastRequestV2,
): Promise<CreateBroadcastResponse> {
  const body: AudiencesCreateBroadcastRequestV2 = { siteId, broadcast };
  return withJWTClient(async (client) => {
    const response = await client.post(
      `${API_BASE_URL}/${siteId}/broadcasts`,
      body,
    );
    return createBroadcastResponseSchema.parse(response.data);
  });
}

// --- List ---

export interface ListBroadcastsParams {
  limit?: number;
  page?: number;
  cursor?: string;
  status?: AudiencesBroadcastStatus;
}

export async function listBroadcastsViaAudiencesApi(
  siteId: string,
  params?: ListBroadcastsParams,
): Promise<ListBroadcastsResponse> {
  return withJWTClient(async (client) => {
    const searchParams = new URLSearchParams();
    if (params?.limit) {
      searchParams.append("pagination.limit", params.limit.toString());
    }
    if (params?.page) {
      searchParams.append("pagination.page", params.page.toString());
    }
    if (params?.cursor) {
      searchParams.append("pagination.cursor", params.cursor);
    }
    if (params?.status) {
      searchParams.append("filter.condition.field", "FILTER_FIELD_STATUS");
      searchParams.append("filter.condition.operator", "FILTER_OPERATOR_EQ");
      searchParams.append("filter.condition.value.string_value", params.status);
    }
    const qs = searchParams.toString();
    const response = await client.get(
      `${API_BASE_URL}/${siteId}/broadcasts${qs ? `?${qs}` : ""}`,
    );
    return listBroadcastsResponseSchema.parse(response.data);
  });
}

// --- Clone ---

export async function cloneBroadcastViaAudiencesApi(
  siteId: string,
  broadcastId: string,
): Promise<CloneBroadcastResponse> {
  return withJWTClient(async (client) => {
    const response = await client.post(
      `${API_BASE_URL}/${siteId}/broadcasts/${broadcastId}:clone`,
      {},
    );
    return cloneBroadcastResponseSchema.parse(response.data);
  });
}

// --- Update Status (archive / unarchive / activate / revert to draft) ---

export async function updateBroadcastStatusViaAudiencesApi(
  siteId: string,
  broadcastId: string,
  status: AudiencesBroadcastStatus,
): Promise<void> {
  return withJWTClient(async (client) => {
    await client.put(
      `${API_BASE_URL}/${siteId}/broadcasts/${broadcastId}/status`,
      { status },
    );
  });
}

// --- Delete (permanently deletes a DRAFT broadcast) ---

export async function deleteBroadcastViaAudiencesApi(
  siteId: string,
  broadcastId: string,
): Promise<void> {
  return withJWTClient(async (client) => {
    await client.delete(`${API_BASE_URL}/${siteId}/broadcasts/${broadcastId}`);
  });
}

// --- Get ---

export async function getBroadcastViaAudiencesApi(
  siteId: string,
  broadcastId: string,
): Promise<GetBroadcastResponse> {
  return withJWTClient(async (client) => {
    const response = await client.get(
      `${API_BASE_URL}/${siteId}/broadcasts/${broadcastId}`,
    );
    return getBroadcastResponseSchema.parse(response.data);
  });
}

// --- Update (consolidated patch-style) ---

export async function updateBroadcastV2ViaAudiencesApi(
  siteId: string,
  broadcastId: string,
  payload: UpdateBroadcastRequestV2,
): Promise<UpdateBroadcastResponse> {
  return withJWTClient(async (client) => {
    const response = await client.put(
      `${API_BASE_URL}/${siteId}/broadcasts/${broadcastId}`,
      payload,
    );
    return updateBroadcastResponseSchema.parse(response.data);
  });
}

// --- Add / Remove Audiences ---

export async function addAudiencesViaAudiencesApi(
  siteId: string,
  broadcastId: string,
  audienceIds: string[],
): Promise<void> {
  return withJWTClient(async (client) => {
    await client.post(
      `${API_BASE_URL}/${siteId}/broadcasts/${broadcastId}/audiences`,
      { audienceIds },
    );
  });
}

export async function removeAudiencesViaAudiencesApi(
  siteId: string,
  broadcastId: string,
  audienceIds: string[],
): Promise<void> {
  return withJWTClient(async (client) => {
    await client.post(
      `${API_BASE_URL}/${siteId}/broadcasts/${broadcastId}/audiences:remove`,
      { audienceIds },
    );
  });
}

// --- Diff utilities ---

export function diffAudienceIds(
  previous: string[],
  next: string[],
): { toAdd: string[]; toRemove: string[] } {
  const prevSet = new Set(previous);
  const nextSet = new Set(next);
  return {
    toAdd: next.filter((id) => !prevSet.has(id)),
    toRemove: previous.filter((id) => !nextSet.has(id)),
  };
}

export function diffVariations(
  original: BroadcastVariation[],
  current: BroadcastVariation[],
): {
  add: BroadcastVariation[];
  update: BroadcastVariation[];
  remove: string[];
} {
  const originalFrameIds = new Set(original.map((v) => v.frameId));
  const currentFrameIds = new Set(current.map((v) => v.frameId));
  return {
    add: current.filter((v) => !originalFrameIds.has(v.frameId)),
    update: current.filter((v) => originalFrameIds.has(v.frameId)),
    remove: original
      .filter((v) => !currentFrameIds.has(v.frameId))
      .map((v) => v.frameId),
  };
}

// --- Get Broadcasts by Frame ---

export interface LinkedBroadcast {
  id: string;
  name: string;
}

export async function getBroadcastsByFrameId(
  siteId: string,
  frameId: string,
): Promise<LinkedBroadcast[]> {
  return withJWTClient(async (client) => {
    const response = await client.get(
      `${API_BASE_URL}/${siteId}/broadcasts/frames/${frameId}`,
    );
    const broadcastIds =
      (response.data as { broadcastIds?: string[] }).broadcastIds ?? [];

    if (broadcastIds.length === 0) return [];

    const details = await Promise.all(
      broadcastIds.map(async (id) => {
        try {
          const detail = await getBroadcastViaAudiencesApi(siteId, id);
          return { id, name: detail.broadcast.name };
        } catch {
          return { id, name: id };
        }
      }),
    );

    return details;
  });
}

// --- React Query keys ---

export const broadcastV2Keys = {
  all: (siteId: string) => ["broadcastV2", siteId] as const,
  list: (siteId: string, params?: ListBroadcastsParams) =>
    ["broadcastV2", siteId, "list", params] as const,
  detail: (siteId: string, broadcastId: string) =>
    ["broadcastV2", siteId, "detail", broadcastId] as const,
  byFrameId: (siteId: string, frameId: string) =>
    ["broadcastV2", siteId, "byFrameId", frameId] as const,
};

// --- React Query options ---

export function broadcastV2ListQueryOptions(
  siteId: string,
  params?: ListBroadcastsParams,
) {
  return {
    queryKey: broadcastV2Keys.list(siteId, params),
    queryFn: () => listBroadcastsViaAudiencesApi(siteId, params),
  };
}

export function linkedBroadcastsForFrameQueryOptions(
  siteId: string,
  frameId: string,
) {
  return {
    queryKey: broadcastV2Keys.byFrameId(siteId, frameId),
    queryFn: () => getBroadcastsByFrameId(siteId, frameId),
  };
}

// --- React Query hooks ---

export function useBroadcastV2List(
  siteId: string,
  params?: ListBroadcastsParams,
) {
  return useSuspenseQuery(broadcastV2ListQueryOptions(siteId, params));
}

// Non-suspense variant for inline pickers (e.g. audience filter rows) that
// aren't wrapped in a Suspense boundary.
export function useBroadcastV2ListQuery(
  siteId: string,
  params?: ListBroadcastsParams,
) {
  return useQuery({
    ...broadcastV2ListQueryOptions(siteId, params),
    enabled: !!siteId,
  });
}

export function useLinkedBroadcasts(
  siteId: string,
  frameId: string | undefined,
) {
  return useQuery({
    ...linkedBroadcastsForFrameQueryOptions(siteId, frameId ?? ""),
    enabled: !!frameId,
  });
}

export function useCloneBroadcastV2(siteId: string) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationKey: ["cloneBroadcastV2", siteId],
    mutationFn: (broadcastId: string) =>
      cloneBroadcastViaAudiencesApi(siteId, broadcastId),
    onSuccess: () => {
      enqueueSnackbar("Broadcast cloned!", { variant: "success" });
      void queryClient.invalidateQueries({
        queryKey: broadcastV2Keys.all(siteId),
      });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to clone broadcast");
    },
  });
}

export function useUpdateBroadcastV2Status(siteId: string) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationKey: ["updateBroadcastV2Status", siteId],
    mutationFn: ({
      broadcastId,
      status,
    }: {
      broadcastId: string;
      status: AudiencesBroadcastStatus;
    }) => updateBroadcastStatusViaAudiencesApi(siteId, broadcastId, status),
    onSuccess: (_, { status }) => {
      const message =
        status === "BROADCAST_STATUS_ARCHIVED"
          ? "Broadcast archived!"
          : "Broadcast status updated!";
      enqueueSnackbar(message, { variant: "success" });
      void queryClient.invalidateQueries({
        queryKey: broadcastV2Keys.all(siteId),
      });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to update broadcast status");
    },
  });
}

export function useDeleteBroadcastV2(siteId: string) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationKey: ["deleteBroadcastV2", siteId],
    mutationFn: (broadcastId: string) =>
      deleteBroadcastViaAudiencesApi(siteId, broadcastId),
    onSuccess: (_, broadcastId) => {
      enqueueSnackbar("Broadcast deleted!", { variant: "success" });
      void queryClient.invalidateQueries({
        queryKey: broadcastV2Keys.all(siteId),
      });
      queryClient.removeQueries({
        queryKey: broadcastV2Keys.detail(siteId, broadcastId),
      });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to delete broadcast");
    },
  });
}

// --- Get Broadcast ---

export function broadcastV2DetailQueryOptions(
  siteId: string,
  broadcastId: string,
) {
  return {
    queryKey: broadcastV2Keys.detail(siteId, broadcastId),
    queryFn: () => getBroadcastViaAudiencesApi(siteId, broadcastId),
  };
}

export function useBroadcastV2Detail(siteId: string, broadcastId: string) {
  return useSuspenseQuery(broadcastV2DetailQueryOptions(siteId, broadcastId));
}

// --- Update Broadcast ---

export function useUpdateBroadcastV2(siteId: string, broadcastId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["updateBroadcastV2", siteId, broadcastId],
    mutationFn: (payload: UpdateBroadcastRequestV2) =>
      updateBroadcastV2ViaAudiencesApi(siteId, broadcastId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: broadcastV2Keys.detail(siteId, broadcastId),
      });
      void queryClient.invalidateQueries({
        queryKey: broadcastV2Keys.all(siteId),
      });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to update broadcast");
    },
  });
}

// --- Add / Remove Audiences hooks ---

export function useAddAudiences(siteId: string, broadcastId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["addAudiences", siteId, broadcastId],
    mutationFn: (audienceIds: string[]) =>
      addAudiencesViaAudiencesApi(siteId, broadcastId, audienceIds),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: broadcastV2Keys.detail(siteId, broadcastId),
      });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to add audiences");
    },
  });
}

export function useRemoveAudiences(siteId: string, broadcastId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["removeAudiences", siteId, broadcastId],
    mutationFn: (audienceIds: string[]) =>
      removeAudiencesViaAudiencesApi(siteId, broadcastId, audienceIds),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: broadcastV2Keys.detail(siteId, broadcastId),
      });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to remove audiences");
    },
  });
}
